import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BookList } from './BookList';
import { ImageReorder } from './ImageReorder';
import { TagManager } from './TagManager';
import WordPressImporter from './WordPressImporter';
import { ExemplarManager } from './ExemplarManager';
import { Book, TagCategory, Tag, CreateBookData, ValidationError, BookImage } from '../types';
import { booksApi, tagsApi, authApi } from '../services/api';
import type { UserData } from '../services/api';
import { usersApi } from '../services/api';
import './AdminPanel.css';

// Estilos inline para melhor legibilidade (paleta Ipsis Pack — índigo/verde)
const styles = {
  label: { display: 'block', marginBottom: '8px', fontWeight: 600, color: '#373a50', fontSize: '0.9375rem' },
  input: { width: '100%', padding: '12px', border: '1px solid #d6d9e6', borderRadius: '8px', boxSizing: 'border-box' as const, fontSize: '0.9375rem', backgroundColor: '#fff', color: '#373a50' },
  textarea: { width: '100%', padding: '12px', border: '1px solid #d6d9e6', borderRadius: '8px', boxSizing: 'border-box' as const, fontSize: '0.9375rem', backgroundColor: '#fff', color: '#373a50', resize: 'vertical' as const },
  field: { marginBottom: '20px' },
  checkbox: { display: 'flex', alignItems: 'center', gap: '10px', color: '#373a50', fontSize: '0.9375rem' },
  button: { padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9375rem', fontWeight: 600 },
  primaryBtn: { background: '#2f8a55', color: 'white' },
  secondaryBtn: { background: '#fff', color: '#373a50', border: '1px solid #d6d9e6' },
  cancelBtn: { background: '#dc2626', color: 'white' },
  modal: { position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(14,11,52,0.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: 'white', borderRadius: '14px', padding: '30px', maxWidth: '600px', width: '90%', maxHeight: '90vh', overflow: 'auto', border: '1px solid #e5e8f1' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e8f1', paddingBottom: '15px' },
  closeBtn: { background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#5a5d7a' },
  imagePreview: { display: 'flex', gap: '10px', flexWrap: 'wrap' as const, marginTop: '10px' },
  imageThumb: { width: '80px', height: '80px', objectFit: 'cover' as const, borderRadius: '6px', border: '2px solid #e5e8f1' },
  imageContainer: { position: 'relative' as const },
  removeImageBtn: { position: 'absolute' as const, top: '-8px', right: '-8px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '13px' }
};

// Componente BookForm com melhorias
interface BookFormProps {
  book?: Book;
  onSubmit?: (book: Book) => void;
  onCancel?: () => void;
  isModal?: boolean;
}

function BookForm({ book, onSubmit, onCancel, isModal }: BookFormProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [publicationYear, setPublicationYear] = useState('');
  const [physicallyAvailable, setPhysicallyAvailable] = useState(false);
  const [isAwarded, setIsAwarded] = useState(false);
  const [boxType, setBoxType] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [categories, setCategories] = useState<TagCategory[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [newImages, setNewImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const [existingImages, setExistingImages] = useState<BookImage[]>([]);
  const isEditMode = !!book;

  useEffect(() => {
    tagsApi.getCategories().then(setCategories).catch(console.error);
    tagsApi.getAllByCategory().then(data => {
      const tags: Tag[] = [];
      data.forEach(item => {
        if (item.tags) {
          tags.push(...item.tags);
        }
      });
      setAllTags(tags);
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (book) {
      setTitle(book.title);
      setDescription(book.description);
      setPublicationYear(book.publicationYear?.toString() || '');
      setPhysicallyAvailable(book.physicallyAvailable);
      setIsAwarded(book.isAwarded || false);
      setBoxType(book.boxType || '');
      setSelectedTagIds(book.tags?.map(t => t.id) || []);
      setExistingImages(book.images || []);
    } else {
      setTitle(''); setDescription(''); setPublicationYear(''); setPhysicallyAvailable(false); setIsAwarded(false); setBoxType(''); setSelectedTagIds([]); setExistingImages([]);
    }
  }, [book]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setNewImages(prev => [...prev, ...files]);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreviews(prev => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
  };

  const removeNewImage = (index: number) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageId: number) => {
    if (book) {
      try {
        await booksApi.removeImage(book.id, imageId);
        setExistingImages(prev => prev.filter(img => img.id !== imageId));
        setMessage('Imagem removida!');
        setTimeout(() => setMessage(null), 2000);
      } catch {
        setMessage('Erro ao remover imagem');
      }
    }
  };

  const handleImageReorder = async (reorderedImages: BookImage[]) => {
    setExistingImages(reorderedImages);

    if (book) {
      try {
        const imageIds = reorderedImages.map(img => img.id);
        await booksApi.reorderImages(book.id, imageIds);
        setMessage('Ordem das imagens atualizada!');
        setTimeout(() => setMessage(null), 2000);
      } catch {
        setMessage('Erro ao reordenar imagens');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: ValidationError[] = [];
    if (!title.trim()) errs.push({ field: 'title', message: 'Titulo obrigatorio' });
    if (!description.trim()) errs.push({ field: 'description', message: 'Descricao obrigatoria' });
    setErrors(errs);
    if (errs.length > 0) return;

    setIsSubmitting(true);
    try {
      // Campos "limpáveis": publicationYear null e boxType '' são enviados de
      // propósito (o backend distinge "ausente" de "vazio"); tagIds sempre
      // presente — array vazio remove todas as tags
      const data = { title: title.trim(), description: description.trim(), publicationYear: publicationYear ? parseInt(publicationYear) : null, physicallyAvailable, isAwarded, boxType, tagIds: selectedTagIds };
      const saved = isEditMode && book ? await booksApi.update(book.id, data, newImages) : await booksApi.create(data as CreateBookData, newImages);
      setMessage(isEditMode ? 'Projeto atualizado!' : 'Projeto cadastrado!');
      if (!isEditMode) { setTitle(''); setDescription(''); setPublicationYear(''); setPhysicallyAvailable(false); setSelectedTagIds([]); setBoxType(''); setNewImages([]); setImagePreviews([]); }
      setTimeout(() => { if (onSubmit) onSubmit(saved); }, 500);
    } catch { setMessage('Erro ao salvar'); }
    finally { setIsSubmitting(false); }
  };

  const formContent = (
    <form onSubmit={handleSubmit}>
      {message && <div style={{padding: '12px', background: message.includes('Erro') ? '#fee2e2' : '#f0faf4', marginBottom: '20px', borderRadius: '8px', color: message.includes('Erro') ? '#b91c1c' : '#206f40'}}>{message}</div>}
      
      <div style={styles.field}>
        <label style={styles.label}>Título *</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} style={styles.input} disabled={isSubmitting} placeholder="Digite o título do projeto" />
        {errors.find(e => e.field === 'title') && <span style={{color: '#dc2626', fontSize: '13px', marginTop: '4px', display: 'block'}}>{errors.find(e => e.field === 'title')?.message}</span>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Descrição *</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} style={styles.textarea} disabled={isSubmitting} placeholder="Digite a descrição do projeto" />
        {errors.find(e => e.field === 'description') && <span style={{color: '#dc2626', fontSize: '13px', marginTop: '4px', display: 'block'}}>{errors.find(e => e.field === 'description')?.message}</span>}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Ano de Publicação</label>
        <input type="number" value={publicationYear} onChange={e => setPublicationYear(e.target.value)} style={{...styles.input, width: '150px'}} disabled={isSubmitting} placeholder="Ex: 2024" />
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Tipo de Caixa</label>
        <select value={boxType} onChange={e => setBoxType(e.target.value)} style={{...styles.input, width: '300px'}} disabled={isSubmitting}>
          <option value="">Selecione o tipo de caixa</option>
          <option value="rigida">Caixa Rígida</option>
          <option value="semirrigida">Caixa Semirrígida</option>
          <option value="hibrida">Caixa Híbrida (Rígida e Semirrígida)</option>
        </select>
      </div>

      <div style={styles.field}>
        <label style={styles.checkbox}><input type="checkbox" checked={physicallyAvailable} onChange={e => setPhysicallyAvailable(e.target.checked)} disabled={isSubmitting} /> Disponível fisicamente na empresa</label>
      </div>

      <div style={styles.field}>
        <label style={styles.checkbox}><input type="checkbox" checked={isAwarded} onChange={e => setIsAwarded(e.target.checked)} disabled={isSubmitting} /> 🏆 Projeto Premiado</label>
      </div>


      <div style={styles.field}>
        <label style={styles.label}>Imagens do Projeto</label>
        <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px'}}>
          <label style={{...styles.button, ...styles.secondaryBtn, display: 'inline-block', cursor: 'pointer'}}>
            📷 Escolher Imagens
            <input type="file" accept="image/*" multiple onChange={handleImageSelect} disabled={isSubmitting} style={{display: 'none'}} />
          </label>
          <span style={{color: '#373a50', fontSize: '0.9375rem'}}>{newImages.length > 0 ? `${newImages.length} arquivo(s) selecionado(s)` : 'Nenhum arquivo selecionado'}</span>
        </div>
        {existingImages.length > 0 && (
          <ImageReorder
            images={existingImages}
            onReorder={handleImageReorder}
            onRemove={removeExistingImage}
          />
        )}
        {imagePreviews.length > 0 && (
          <div style={styles.imagePreview}>
            {imagePreviews.map((preview, i) => (
              <div key={`new-${i}`} style={styles.imageContainer}>
                <img src={preview} alt={`Nova ${i+1}`} style={styles.imageThumb} />
                <button type="button" onClick={() => removeNewImage(i)} style={styles.removeImageBtn}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Tags</label>
        {categories.map(cat => {
          const tags = allTags.filter(t => t.categoryId === cat.id);
          if (!tags.length) return null;
          return (
            <div key={cat.id} style={{marginBottom: '15px', padding: '10px', background: '#f4f5fa', borderRadius: '6px'}}>
              <strong style={{color: '#191364', display: 'block', marginBottom: '8px'}}>{cat.displayName}</strong>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px'}}>
                {tags.map(tag => (
                  <label key={tag.id} style={{...styles.checkbox, background: selectedTagIds.includes(tag.id) ? '#f0faf4' : 'white', padding: '6px 12px', borderRadius: '20px', border: selectedTagIds.includes(tag.id) ? '1px solid #cbe9d8' : '1px solid #d6d9e6', color: selectedTagIds.includes(tag.id) ? '#206f40' : 'inherit'}}>
                    <input type="checkbox" checked={selectedTagIds.includes(tag.id)} onChange={() => setSelectedTagIds(prev => prev.includes(tag.id) ? prev.filter(id => id !== tag.id) : [...prev, tag.id])} disabled={isSubmitting} />
                    {tag.name}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e8f1'}}>
        {onCancel && <button type="button" onClick={onCancel} disabled={isSubmitting} style={{...styles.button, ...styles.cancelBtn}}>Cancelar</button>}
        <button type="submit" disabled={isSubmitting} style={{...styles.button, ...styles.primaryBtn}}>{isSubmitting ? 'Salvando...' : isEditMode ? 'Salvar Alterações' : 'Cadastrar Projeto'}</button>
      </div>
    </form>
  );

  if (isModal && onCancel) {
    return (
      <div style={styles.modal} onClick={onCancel}>
        <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div style={styles.modalHeader}>
            <h2 style={{margin: 0, color: '#191364'}}>{isEditMode ? '✏️ Editar Projeto' : '📦 Cadastrar Novo Projeto'}</h2>
            <button onClick={onCancel} style={styles.closeBtn}>×</button>
          </div>
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div style={{padding: '25px', background: 'white', borderRadius: '14px', boxShadow: '0 2px 8px rgba(38,40,62,0.1)', marginBottom: '25px'}}>
      <h2 style={{marginTop: 0, color: '#191364', marginBottom: '25px'}}>📦 Cadastrar Novo Projeto</h2>
      {formContent}
    </div>
  );
}


// Tipos para navegação
type AdminSection = 'books' | 'tags' | 'exemplars' | 'import' | 'users';

// Constantes para autenticação
const SESSION_KEY = 'admin_session';

interface SessionData {
  username: string;
  loginTime: number;
  lastActivity: number;
  role?: string;
}

interface LoginFormProps {
  onLogin: (username: string, password: string) => Promise<boolean>;
  error: string | null;
  isLoading: boolean;
}

function LoginForm({ onLogin, error, isLoading }: LoginFormProps): React.ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onLogin(username, password);
  };

  return (
    <div className="admin-panel__login-container">
      <div className="admin-panel__login-card">
        <div className="admin-panel__login-header">
          <h1 className="admin-panel__login-title">📦 Estante Ipsis Pack</h1>
          <p className="admin-panel__login-subtitle">Painel Administrativo</p>
        </div>

        <form className="admin-panel__login-form" onSubmit={handleSubmit}>
          {error && (
            <div className="admin-panel__login-error">
              {error}
            </div>
          )}

          <div className="admin-panel__login-field">
            <label htmlFor="username" className="admin-panel__login-label">
              Usuário
            </label>
            <input
              type="text"
              id="username"
              className="admin-panel__login-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Digite seu usuário"
              disabled={isLoading}
              autoComplete="username"
              required
            />
          </div>

          <div className="admin-panel__login-field">
            <label htmlFor="password" className="admin-panel__login-label">
              Senha
            </label>
            <input
              type="password"
              id="password"
              className="admin-panel__login-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite sua senha"
              disabled={isLoading}
              autoComplete="current-password"
              required
            />
          </div>

          <button
            type="submit"
            className="admin-panel__login-button"
            disabled={isLoading || !username.trim() || !password.trim()}
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    </div>
  );
}


export function AdminPanel(): React.ReactElement {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [session, setSession] = useState<SessionData | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [activeSection, setActiveSection] = useState<AdminSection>('books');
  const [editingBook, setEditingBook] = useState<Book | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Verifica sessão ao carregar — a fonte da verdade é o SERVIDOR
  // (sessão deslizante com timeout configurável via SESSION_TIMEOUT)
  useEffect(() => {
    let cancelled = false;

    const checkSession = async (): Promise<void> => {
      const storedSession = localStorage.getItem(SESSION_KEY);
      try {
        const status = await authApi.getStatus();
        if (cancelled) return;

        if (status.isAuthenticated) {
          const base: SessionData = storedSession
            ? JSON.parse(storedSession)
            : { username: status.user?.username || '', loginTime: Date.now(), lastActivity: Date.now() };
          const sessionData: SessionData = { ...base, username: status.user?.username || base.username, lastActivity: Date.now() };
          localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
          setSession(sessionData);
          setUserRole(status.user?.role || null);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem(SESSION_KEY);
          setUserRole(null);
        }
      } catch {
        // Servidor inacessível: mantém sessão local otimista — as chamadas
        // de API vão falhar e o interceptor de 401 cuida da volta ao login
        if (!cancelled && storedSession) {
          try {
            const sessionData: SessionData = JSON.parse(storedSession);
            setSession(sessionData);
            setIsAuthenticated(true);
          } catch {
            localStorage.removeItem(SESSION_KEY);
          }
        }
      }
    };

    void checkSession();
    return () => { cancelled = true; };
  }, []);

  const handleLogin = useCallback(async (username: string, password: string): Promise<boolean> => {
    setIsLoggingIn(true);
    setLoginError(null);

    try {
      const result = await authApi.login(username, password);
      const sessionData: SessionData = {
        username: result.user.username,
        loginTime: Date.now(),
        lastActivity: Date.now(),
        role: result.user.role,
      };

      localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
      setSession(sessionData);
      setUserRole(result.user.role || null);
      setIsAuthenticated(true);
      return true;
    } catch (err: any) {
      const message = err?.message || err?.response?.data?.error?.message || 'Usuário ou senha inválidos';
      setLoginError(message);
      return false;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const handleLogout = useCallback((): void => {
    // Encerra a sessão no servidor em segundo plano (best-effort, sem travar
    // a UI se a rede estiver ruim) antes da limpeza local
    authApi.logout().catch(() => {
      // sessão já inválida ou servidor inacessível — o cookie expira sozinho
    });
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setUserRole(null);
    setIsAuthenticated(false);
    setActiveSection('books');
    setEditingBook(null);
  }, []);

  const handleEditBook = useCallback((book: Book) => {
    setEditingBook(book);
    setActiveSection('books');
  }, []);

  const handleBookSubmit = useCallback((book: Book) => {
    setEditingBook(null);
    setRefreshKey((prev) => prev + 1);
    console.log('Projeto salvo:', book.title);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingBook(null);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const handleSectionChange = useCallback((section: AdminSection) => {
    setActiveSection(section);
    if (section !== 'books') {
      setEditingBook(null);
    }
  }, []);

  // Renderiza tela de login se não autenticado
  if (!isAuthenticated) {
    return (
      <LoginForm
        onLogin={handleLogin}
        error={loginError}
        isLoading={isLoggingIn}
      />
    );
  }

  // A API já bloqueia viewers (403) em todas as seções de gestão; esta checagem
  // evita renderizar um painel quebrando com erros para quem não é admin
  // (mesmo padrão do ExemplarStockView). Sem role conhecida (servidor offline,
  // sessão local otimista) não bloqueia — o interceptor de 403/401 cuida.
  if (userRole && userRole !== 'admin') {
    return (
      <div className="admin-panel">
        <header className="admin-panel__header">
          <div className="admin-panel__header-content">
            <h1 className="admin-panel__logo">📦 Estante Ipsis Pack</h1>
            <div className="admin-panel__user-info">
              <span className="admin-panel__username">
                Olá, {session?.username}
              </span>
              <button
                type="button"
                className="admin-panel__logout-button"
                onClick={handleLogout}
              >
                Sair
              </button>
            </div>
          </div>
        </header>
        <div className="admin-panel__login-container">
          <div className="admin-panel__login-card">
            <div style={{ textAlign: 'center', padding: '1rem 0.5rem' }}>
              <span style={{ fontSize: '3rem' }}>🔒</span>
              <h2 style={{ margin: '0.5rem 0', color: '#191364', fontFamily: 'var(--font-family-display)' }}>
                Acesso restrito
              </h2>
              <p style={{ color: '#5a5d7a', margin: '0 0 1.5rem' }}>
                A área administrativa é exclusiva de administradores.
              </p>
              <Link
                to="/"
                style={{ color: '#2f8a55', fontWeight: 600, textDecoration: 'none' }}
              >
                ← Voltar ao catálogo
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      {/* Header */}
      <header className="admin-panel__header">
        <div className="admin-panel__header-content">
          <h1 className="admin-panel__logo">📦 Estante Ipsis Pack</h1>
          <div className="admin-panel__user-info">
            <span className="admin-panel__username">
              Olá, {session?.username}
            </span>
            <Link to="/" className="admin-panel__catalog-link" title="Abrir o catálogo de projetos">
              ← <span className="admin-panel__catalog-link-text">Ver catálogo</span>
            </Link>
            <button
              type="button"
              className="admin-panel__logout-button"
              onClick={handleLogout}
            >
              Sair
            </button>
          </div>
        </div>
      </header>

      <div className="admin-panel__container">
        {/* Sidebar Navigation */}
        <nav className="admin-panel__sidebar">
          <ul className="admin-panel__nav-list">
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'books' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('books')}
              >
                <span className="admin-panel__nav-icon">📦</span>
                <span className="admin-panel__nav-text">Projetos</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'tags' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('tags')}
              >
                <span className="admin-panel__nav-icon">🏷️</span>
                <span className="admin-panel__nav-text">Tags</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'exemplars' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('exemplars')}
              >
                <span className="admin-panel__nav-icon">📦</span>
                <span className="admin-panel__nav-text">Estoque Mauá</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'import' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('import')}
              >
                <span className="admin-panel__nav-icon">📥</span>
                <span className="admin-panel__nav-text">Importação</span>
              </button>
            </li>
            <li className="admin-panel__nav-item">
              <button
                type="button"
                className={`admin-panel__nav-button ${
                  activeSection === 'users' ? 'admin-panel__nav-button--active' : ''
                }`}
                onClick={() => handleSectionChange('users')}
              >
                <span className="admin-panel__nav-icon">👥</span>
                <span className="admin-panel__nav-text">Usuários</span>
              </button>
            </li>
          </ul>
        </nav>

        {/* Main Content */}
        <main className="admin-panel__main">
          {activeSection === 'books' && (
            <div className="admin-panel__section">
              {/* Formulário para NOVO livro - sempre visível */}
              <BookForm
                onSubmit={handleBookSubmit}
              />
              <div className="admin-panel__section-divider" />
              <BookList
                key={refreshKey}
                onEdit={handleEditBook}
                onRefresh={handleRefresh}
              />
              {/* Modal para EDITAR livro - só aparece quando editingBook existe */}
              {editingBook && (
                <BookForm
                  book={editingBook}
                  onSubmit={handleBookSubmit}
                  onCancel={handleCancelEdit}
                  isModal={true}
                />
              )}
            </div>
          )}

          {activeSection === 'tags' && (
            <div className="admin-panel__section">
              <TagManager onTagsChange={handleRefresh} />
            </div>
          )}

          {activeSection === 'exemplars' && (
            <div className="admin-panel__section">
              <ExemplarManager />
            </div>
          )}

          {activeSection === 'import' && (
            <div className="admin-panel__section">
              <WordPressImporter onImportComplete={() => setRefreshKey(prev => prev + 1)} />
            </div>
          )}

          {activeSection === 'users' && (
            <div className="admin-panel__section">
              <UserManager />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// Componente de gerenciamento de usuários
function UserManager(): React.ReactElement {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'viewer'>('viewer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Estado de edição
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editRole, setEditRole] = useState<'admin' | 'viewer'>('viewer');
  const [isEditing, setIsEditing] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) return;
    setIsSubmitting(true);
    try {
      await usersApi.create({ username: newUsername.trim(), password: newPassword, displayName: newDisplayName.trim() || newUsername.trim(), role: newRole });
      setMessage('Usuário criado com sucesso!');
      setNewUsername(''); setNewPassword(''); setNewDisplayName(''); setNewRole('viewer');
      setShowCreateForm(false);
      loadUsers();
    } catch (err: any) {
      setMessage(err?.message || 'Erro ao criar usuário');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleEditClick = (user: UserData) => {
    setEditingUser(user);
    setEditUsername(user.username);
    setEditDisplayName(user.displayName);
    setEditRole(user.role);
    setEditPassword('');
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    setIsEditing(true);
    try {
      const updateData: any = {};
      if (editUsername.trim() !== editingUser.username) updateData.username = editUsername.trim();
      if (editDisplayName.trim() !== editingUser.displayName) updateData.displayName = editDisplayName.trim();
      if (editRole !== editingUser.role) updateData.role = editRole;
      if (editPassword.trim()) updateData.password = editPassword;

      await usersApi.update(editingUser.id, updateData);
      setMessage('Usuário atualizado com sucesso!');
      setEditingUser(null);
      loadUsers();
    } catch (err: any) {
      setMessage(err?.message || 'Erro ao atualizar usuário');
    } finally {
      setIsEditing(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleToggleActive = async (user: UserData) => {
    try {
      await usersApi.update(user.id, { isActive: !user.isActive });
      loadUsers();
    } catch (err: any) {
      setMessage(err?.message || 'Erro ao atualizar usuário');
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleDelete = async (user: UserData) => {
    if (!confirm(`Tem certeza que deseja remover o usuário "${user.username}"?`)) return;
    try {
      await usersApi.delete(user.id);
      setMessage(`Usuário "${user.username}" removido.`);
      loadUsers();
    } catch (err: any) {
      setMessage(err?.message || 'Erro ao remover usuário');
    }
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div style={{ background: 'white', borderRadius: '14px', padding: '25px', boxShadow: '0 2px 8px rgba(38,40,62,0.1)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#191364', display: 'flex', alignItems: 'center', gap: '8px' }}>👥 Gerenciar Usuários</h2>
        <button onClick={() => { setEditingUser(null); setShowCreateForm(!showCreateForm); }} style={{ padding: '10px 18px', background: '#2f8a55', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '0.9375rem' }}>
          + Novo Usuário
        </button>
      </div>

      {message && (
        <div style={{ padding: '12px', background: message.includes('Erro') ? '#fee2e2' : '#f0faf4', marginBottom: '20px', borderRadius: '8px', color: message.includes('Erro') ? '#b91c1c' : '#206f40' }}>
          {message}
        </div>
      )}

      {/* Formulário de novo usuário (toggle) */}
      {showCreateForm && (
        <form onSubmit={handleCreate} style={{ marginBottom: '25px', padding: '20px', background: '#f4f5fa', borderRadius: '8px', border: '1px solid #e5e8f1' }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#191364', fontSize: '1rem' }}>Novo Usuário</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="Nome de usuário *" disabled={isSubmitting} style={{ padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }} required />
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Senha *" disabled={isSubmitting} style={{ padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }} required />
            <input type="text" value={newDisplayName} onChange={e => setNewDisplayName(e.target.value)} placeholder="Nome de exibição" disabled={isSubmitting} style={{ padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }} />
            <select value={newRole} onChange={e => setNewRole(e.target.value as 'admin' | 'viewer')} disabled={isSubmitting} style={{ padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }}>
              <option value="viewer">Visualizador</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button type="submit" disabled={isSubmitting || !newUsername.trim() || !newPassword.trim()} style={{ padding: '10px 20px', background: '#2f8a55', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
              {isSubmitting ? 'Criando...' : 'Criar Usuário'}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)} style={{ padding: '10px 20px', background: '#fff', color: '#373a50', border: '1px solid #d6d9e6', borderRadius: '8px', cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Modal de edição */}
      {editingUser && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(14,11,52,0.66)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '30px', maxWidth: '450px', width: '90%' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#191364' }}>✏️ Editar Usuário</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9375rem', color: '#373a50' }}>Nome de usuário</label>
                <input type="text" value={editUsername} onChange={e => setEditUsername(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9375rem', color: '#373a50' }}>Nome de exibição</label>
                <input type="text" value={editDisplayName} onChange={e => setEditDisplayName(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9375rem', color: '#373a50' }}>Nova senha (deixe vazio para manter)</label>
                <input type="password" value={editPassword} onChange={e => setEditPassword(e.target.value)} placeholder="Nova senha" style={{ width: '100%', padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }} />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9375rem', color: '#373a50' }}>Papel</label>
                <select value={editRole} onChange={e => setEditRole(e.target.value as 'admin' | 'viewer')} style={{ width: '100%', padding: '10px', border: '1px solid #d6d9e6', borderRadius: '8px' }}>
                  <option value="viewer">Visualizador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setEditingUser(null)} disabled={isEditing} style={{ padding: '10px 20px', background: '#fff', color: '#373a50', border: '1px solid #d6d9e6', borderRadius: '8px', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={handleEditSave} disabled={isEditing || !editUsername.trim()} style={{ padding: '10px 20px', background: '#2f8a55', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                {isEditing ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabela de usuários */}
      {isLoading ? (
        <p>Carregando...</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px' }}>
          <thead>
            <tr>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#5a5d7a', fontSize: '0.875rem', fontWeight: 600, fontStyle: 'italic' }}>Usuário</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', color: '#5a5d7a', fontSize: '0.875rem', fontWeight: 600, fontStyle: 'italic' }}>Nome</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', color: '#5a5d7a', fontSize: '0.875rem', fontWeight: 600, fontStyle: 'italic' }}>Papel</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', color: '#5a5d7a', fontSize: '0.875rem', fontWeight: 600, fontStyle: 'italic' }}>Status</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', color: '#5a5d7a', fontSize: '0.875rem', fontWeight: 600, fontStyle: 'italic' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ background: '#fafbfd', borderRadius: '8px' }}>
                <td style={{ padding: '14px 16px', color: '#41445e', borderTop: '1px solid #e5e8f1', borderBottom: '1px solid #e5e8f1' }}>{user.username}</td>
                <td style={{ padding: '14px 16px', color: '#373a50', fontWeight: 500, borderTop: '1px solid #e5e8f1', borderBottom: '1px solid #e5e8f1' }}>{user.displayName}</td>
                <td style={{ padding: '14px 16px', textAlign: 'center', borderTop: '1px solid #e5e8f1', borderBottom: '1px solid #e5e8f1' }}>
                  <span style={{ padding: '5px 12px', borderRadius: '14px', fontSize: '0.8rem', fontWeight: 600, background: user.role === 'admin' ? '#fef3c7' : '#e9eaf6', color: user.role === 'admin' ? '#b45309' : '#191364' }}>
                    {user.role === 'admin' ? '🔑 Admin' : '👁️ Visualizador'}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'center', borderTop: '1px solid #e5e8f1', borderBottom: '1px solid #e5e8f1' }}>
                  <span style={{ padding: '5px 12px', borderRadius: '14px', fontSize: '0.8rem', fontWeight: 600, background: user.isActive ? '#f0faf4' : '#fee2e2', color: user.isActive ? '#206f40' : '#dc2626' }}>
                    {user.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', textAlign: 'center', borderTop: '1px solid #e5e8f1', borderBottom: '1px solid #e5e8f1' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    <button onClick={() => handleEditClick(user)} style={{ width: '34px', height: '34px', background: '#fef3c7', color: '#b45309', border: 'none', borderRadius: '6px', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Editar">
                      ✏️
                    </button>
                    <button onClick={() => handleToggleActive(user)} style={{ width: '34px', height: '34px', background: user.isActive ? '#fee2e2' : '#f0faf4', color: user.isActive ? '#dc2626' : '#206f40', border: 'none', borderRadius: '6px', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title={user.isActive ? 'Desativar' : 'Ativar'}>
                      {user.isActive ? '🚫' : '✅'}
                    </button>
                    <button onClick={() => handleDelete(user)} style={{ width: '34px', height: '34px', background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: '6px', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Remover">
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminPanel;
